//Library Imports
import { RootState } from 'store';
import styled from 'styled-components';
import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

//Local Imports
import ListingItem from './ListingItem';
import useQuery from '../../../hooks/useQuery';
import { IUserWorkflowData } from '../interfaces';
import EmptyTabInfo from 'modules/Content/Shared/EmptyTabInfo';
import Checkbox from '../../../modules/Common/Components/Checkbox';
import ConfirmDialog from 'modules/Common/Components/ConfirmDialog';
import SelectedBottom from 'modules/Common/Components/SelectedBottom';
import InfiniteLoader from 'modules/Common/Components/InfiniteLoader';
import AutomationSkeleton from 'modules/Content/Shared/AutomationSkeleton';
import { deleteWorkflow, resetWorkflow, resetWorkflowEmailsAnalytics, updateWorkflowErrorUpdate } from '../actions';

interface IListingTable {
  selectedWorkflowIds: number[];
  getUserWorkflowsNext: () => void;
  clearSelectedWorkflowIds: () => void;
  setSelectedWorkflowIds: (value: number[]) => void;
}

const ListingTable: React.FC<IListingTable> = (props: IListingTable) => {
  // Props
  const { getUserWorkflowsNext, selectedWorkflowIds, setSelectedWorkflowIds } = props;

  // Redux States
  const workflowId = useSelector((state: RootState) => state.automation.workflow.detail.id);
  const page: number = useSelector((state: RootState) => state.automation.userWorkflows.page);
  const searchTerm: string = useSelector((state: RootState) => state.automation.userWorkflows.searchTerm);
  const filterType: string = useSelector((state: RootState) => state.automation.userWorkflows.filterType);
  const updateWorkflowError: boolean = useSelector((state: RootState) => state.automation.updateWorkflow.error);
  const userWorkflowsLoading: boolean = useSelector((state: RootState) => state.automation.userWorkflows.loading);
  const deleteWorkflowLoading: boolean = useSelector((state: RootState) => state.automation.deleteWorkflow.loading);
  const updateWorkflowLoading: boolean = useSelector((state: RootState) => state.automation.updateWorkflow.loading);
  const workflowsSearchSuccess: boolean = useSelector((state: RootState) => state.automation.userWorkflows.success); // For Alpha case in search
  const userWorkflowsTotalCount: number = useSelector((state: RootState) => state.automation.userWorkflows.totalCount);
  const userWorkflowsData: IUserWorkflowData[] = useSelector((state: RootState) => state.automation.userWorkflows.data);
  const workflowsSearchErrorMessage: string = useSelector((state: RootState) => state.automation.userWorkflows.message); // For Alpha case in search
  const searchedUserWorkflowsData: IUserWorkflowData[] = useSelector((state: RootState) => state.automation.userWorkflows.searchedData);

  // Local States
  const [showError, setShowError] = useState(false);
  const [isSearched, setIsSearched] = useState(false);
  const [selectAllFlag, setSelectAllFlag] = useState(false);
  const [excludedWorkflowsIds, setExcludedWorkflowsIds] = useState<number[]>([]);
  const [selectableWorkflowsCount, setSelectableWorkflowsCount] = useState<number>(0);

  // Local Variables
  const history = useHistory();
  const dispatch = useDispatch();

  // Component Logic
  useEffect(() => {
    if (workflowId) {
      dispatch(resetWorkflow());
      resetWorkflowEmailsAnalytics();
    }
  }, []);

  // For α (Alpha) case in search
  useEffect(() => {
    if (!workflowsSearchSuccess) {
      setIsSearched(true);
    } else {
      setIsSearched(false);
    }
  }, [workflowsSearchSuccess]);

  useEffect(() => {
    clearSelectedWorkflowsHandler();
    if (searchTerm.length > 0 || filterType.length > 0) {
      setIsSearched(true);
    } else {
      setIsSearched(false);
    }
  }, [searchTerm, filterType]);

  // added this useEffect to avoid the following bug
  // If select all workflows from workflows listing and then
  // delete only one workflow from listing,
  // then the overall counter is not updated at bottom of workflows listing
  useEffect(() => {
    if (!deleteWorkflowLoading) {
      const updatedList = userWorkflowsData.filter((workflow) => selectedWorkflowIds.includes(workflow.id)).map((workflow) => workflow.id);
      setSelectedWorkflowIds(updatedList);
    }
  }, [deleteWorkflowLoading]);

  const selectAllWorkflowIds = (checked: boolean) => {
    if (checked) {
      if (isSearched) {
        setSelectedWorkflowIds(searchedUserWorkflowsData.filter((workflow) => workflow.status !== 'active').map((workflow) => workflow.id));
      } else {
        setSelectedWorkflowIds(userWorkflowsData.filter((workflow) => workflow.status !== 'active').map((workflow) => workflow.id));
      }
      setSelectAllFlag(true);
    } else {
      setSelectedWorkflowIds([]);
      setExcludedWorkflowsIds([]);
      setSelectAllFlag(false);
    }
  };

  const deleteWorkflowHandler = () => {
    deleteWorkflow(selectedWorkflowIds, isSearched, selectAllFlag, excludedWorkflowsIds, searchTerm, filterType);
    setSelectedWorkflowIds([]);
    setSelectAllFlag(false);
  };

  const clearSelectedWorkflowsHandler = () => {
    setSelectedWorkflowIds([]);
    setExcludedWorkflowsIds([]);
    setSelectAllFlag(false);
  };

  const next = () => {
    getUserWorkflowsNext();
  };

  useEffect(() => {
    if (updateWorkflowError) setShowError(true);
  }, [updateWorkflowError]);

  let query = useQuery();
  const fromId = query.get('fromId');

  useEffect(() => {
    if (fromId) {
      const element = document.getElementById(fromId);
      if (element) {
        const headerHeight = document.getElementById('sticky-header')?.clientHeight || 0; // Replace 'header' with the ID or class of your fixed header element
        const elementTop = element.getBoundingClientRect().top;
        const offsetPosition = elementTop - headerHeight;

        window.scrollTo({
          top: window.pageYOffset + offsetPosition,
          behavior: 'smooth',
        });
      }
      query.delete('fromId');
      history.replace({
        search: query.toString(),
      });
    }
  }, []);

  useEffect(() => {
    if (selectAllFlag) {
      if (isSearched) {
        const selectableWorkflowsLength = searchedUserWorkflowsData.filter((workflow) => workflow.status !== 'active').length;
        setSelectableWorkflowsCount(selectableWorkflowsLength);
        setSelectedWorkflowIds(
          searchedUserWorkflowsData
            .filter((workflow: IUserWorkflowData) => !excludedWorkflowsIds.includes(workflow.id) && workflow.status !== 'active')
            .map((workflow: IUserWorkflowData) => workflow.id),
        );
      } else {
        const selectableWorkflowsLength = userWorkflowsData.filter((workflow) => workflow.status !== 'active').length;
        setSelectableWorkflowsCount(selectableWorkflowsLength);
        setSelectedWorkflowIds(
          userWorkflowsData
            .filter((workflow: IUserWorkflowData) => !excludedWorkflowsIds.includes(workflow.id) && workflow.status !== 'active')
            .map((workflow: IUserWorkflowData) => workflow.id),
        );
      }
    } else {
      const selectableWorkflows = userWorkflowsData.filter((workflow) => workflow.status !== 'active').length;
      setSelectableWorkflowsCount(selectableWorkflows);
    }
  }, [searchedUserWorkflowsData, userWorkflowsData, selectAllFlag]);

  return (
    <>
      <Wrapper className="set-table-height-with-footer">
        {/* Toggle class for show/hide footer "set-table-height-with-footer" 
        AND Toggle class on body "gillRoy-index" */}
        <InfiniteLoader
          dataLength={isSearched ? searchedUserWorkflowsData.length + 1 : userWorkflowsData.length + 1}
          next={next}
          hasMore={isSearched ? userWorkflowsTotalCount > searchedUserWorkflowsData.length : userWorkflowsTotalCount > userWorkflowsData.length}
          loader={userWorkflowsLoading && page > 0 ? <AutomationSkeleton /> : <></>}
        >
          <div hidden={isSearched ? searchedUserWorkflowsData.length === 0 : userWorkflowsData.length === 0} className="label-row">
            Workflows
          </div>

          <table className={deleteWorkflowLoading || updateWorkflowLoading ? 'disabled' : ''}>
            <thead hidden={isSearched ? searchedUserWorkflowsData.length === 0 : userWorkflowsData.length === 0}>
              <tr>
                <td>
                  <Checkbox
                    checked={
                      isSearched
                        ? searchedUserWorkflowsData.length > 0 &&
                          selectableWorkflowsCount > 0 &&
                          selectableWorkflowsCount === selectedWorkflowIds.length
                        : userWorkflowsData.length > 0 && selectableWorkflowsCount > 0 && selectableWorkflowsCount === selectedWorkflowIds.length
                    }
                    onChange={(e: any) => selectAllWorkflowIds(e.target.checked)}
                    disabled={
                      (userWorkflowsLoading && page === 0) ||
                      deleteWorkflowLoading ||
                      updateWorkflowLoading ||
                      filterType === 'active' ||
                      selectableWorkflowsCount === 0
                    }
                  />
                </td>
                <td></td>
                <td>Name</td>
                <td></td>
              </tr>
            </thead>
            <tbody>
              {userWorkflowsLoading && page === 0 ? (
                <AutomationSkeleton />
              ) : isSearched ? (
                searchedUserWorkflowsData.length > 0 ? (
                  searchedUserWorkflowsData.map((userWorkflow: IUserWorkflowData) => (
                    <ListingItem
                      key={userWorkflow.id}
                      workflowId={userWorkflow.id}
                      triggerId={userWorkflow.trigger_id}
                      selectedEmailId={userWorkflow.email_id}
                      triggerEmailSent={userWorkflow.trigger_email_sent}
                      shareProfileUuid={userWorkflow.sp_uuid}
                      name={userWorkflow.name}
                      color={userWorkflow.status === 'active' ? '#1EC252' : '#CACACA'}
                      status={userWorkflow.status?.toLowerCase()}
                      selectedWorkflowIds={selectedWorkflowIds}
                      setSelectedWorkflowIds={setSelectedWorkflowIds}
                      isSearched={false}
                      selectAllFlag={selectAllFlag}
                      setSelectAllFlag={setSelectAllFlag}
                      excludedWorkflowsIds={excludedWorkflowsIds}
                      setExcludedWorkflowsIds={setExcludedWorkflowsIds}
                      selectedListId={userWorkflow.list_id}
                    />
                  ))
                ) : (
                  <EmptyTabInfo description={workflowsSearchSuccess ? 'No Workflows matched against your search' : workflowsSearchErrorMessage} />
                )
              ) : userWorkflowsData.length > 0 ? (
                userWorkflowsData.map((userWorkflow: IUserWorkflowData) => (
                  <ListingItem
                    key={userWorkflow.id}
                    workflowId={userWorkflow.id}
                    triggerId={userWorkflow.trigger_id}
                    selectedEmailId={userWorkflow.email_id}
                    triggerEmailSent={userWorkflow.trigger_email_sent}
                    shareProfileUuid={userWorkflow.sp_uuid}
                    name={userWorkflow.name}
                    color={userWorkflow.status === 'active' ? '#1EC252' : '#CACACA'}
                    status={userWorkflow.status?.toLowerCase()}
                    selectedWorkflowIds={selectedWorkflowIds}
                    setSelectedWorkflowIds={setSelectedWorkflowIds}
                    isSearched={false}
                    selectAllFlag={selectAllFlag}
                    setSelectAllFlag={setSelectAllFlag}
                    excludedWorkflowsIds={excludedWorkflowsIds}
                    setExcludedWorkflowsIds={setExcludedWorkflowsIds}
                    selectedListId={userWorkflow.list_id}
                  />
                ))
              ) : (
                <EmptyTabInfo
                  description={
                    'Your Workflows will show here, please click on the “New Workflow +” button at the top to start building your workflow'
                  }
                />
              )}
            </tbody>
          </table>
        </InfiniteLoader>
        <SelectedBottom
          onClearSelection={clearSelectedWorkflowsHandler}
          selectedCount={selectedWorkflowIds.length}
          selectableWorkflowsCount={selectableWorkflowsCount}
          actionText="Delete"
          confirmTitle={`${
            selectAllFlag && excludedWorkflowsIds && excludedWorkflowsIds.length === 0
              ? selectableWorkflowsCount
              : selectAllFlag && excludedWorkflowsIds && excludedWorkflowsIds.length > 0
              ? selectableWorkflowsCount - excludedWorkflowsIds.length
              : selectedWorkflowIds.length
          } selected Workflow(s) will be deleted.`}
          askConfirmDelete={true}
          actionHandler={deleteWorkflowHandler}
          loading={deleteWorkflowLoading}
          selectAllFlag={selectAllFlag}
          setSelectAllFlag={setSelectAllFlag}
          excludedWorkflowsIds={excludedWorkflowsIds}
        />
      </Wrapper>
      {showError ? (
        <DialogWrapper>
          <BackOverlay />
          <ConfirmDialog
            confirm={'Okay'}
            onConfirm={() => {
              setShowError(false);
              updateWorkflowErrorUpdate();
            }}
            title={'Please remove errors from your workflow before you could activate it'}
            titleQuestion={null}
          />
        </DialogWrapper>
      ) : null}
    </>
  );
};

export default ListingTable;

const BackOverlay = styled.div`
  position: fixed;
  background: grey;
  opacity: 0.2;
  height: 100vh;
  width: 100vw;
  top: 0;
  left: 0;
`;

const DialogWrapper = styled.div`
  z-index: 17;
  top: 50%;
  left: 50%;
  position: fixed;
`;

const Wrapper = styled.div`
  &.set-table-height-with-footer {
    padding-bottom: 85px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      padding-bottom: 65px;
    }
  }
  .disabled {
    pointer-events: none;
    opacity: 0.5;
  }
  .floating-footer {
    z-index: 9;
  }
  table {
    width: 100%;
    table-layout: fixed;
    thead {
      td {
        color: #5e7079;
        font-weight: 500;
        font-size: 16px;
        font-family: 'Roboto', sans-serif;
      }
    }
    tr {
      border-bottom: 1px solid #dddddd;
      td {
        padding: 16px 0;
        &:nth-child(1) {
          padding-left: 16px;
          padding-right: 32px;
          width: 20px;
        }
        &:nth-child(2) {
          width: 110px;
          padding-left: 0;
        }
        &:nth-child(3) {
          padding-left: 35px;
        }
        &:nth-child(4) {
          margin-top: 25px;
          padding-left: 16px;
          padding-right: 20px;
          width: 150px;
        }
      }
    }
  }
`;
